import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Maximize2, Download, ChevronDown, Loader2, ImagePlus } from 'lucide-react';
import { MdArrowBackIosNew, MdArrowForwardIos } from 'react-icons/md';
import PhotoUpload from '../Uploads/PhotoUpload';
import { useAuth } from '../../../Context/AuthContext';

interface GalleryItem {
    url: string;
    label?: string;
}

interface OpenGalleryModalProps {
    restaurantId?: string;
    name: string;
    images: GalleryItem[];
    isGalleryOpen: boolean;
    setIsGalleryOpen: (isOpen: boolean) => void;
    initialImageIndex?: number;
    getMorePhotos?: () => Promise<void>;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    enableInfiniteScroll?: boolean; // Toggle between button and auto-scroll
}

const OpenGalleryModal: React.FC<OpenGalleryModalProps> = ({
    restaurantId,
    images,
    name,
    isGalleryOpen,
    setIsGalleryOpen,
    initialImageIndex = 0,
    getMorePhotos,
    hasMore = false,
    isLoadingMore = false,
    enableInfiniteScroll = false,
}) => {
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [masonryColumns, setMasonryColumns] = useState<Array<Array<GalleryItem & { index: number }>>>([]);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const lightboxRef = useRef<HTMLDivElement>(null);
    const masonryContainerRef = useRef<HTMLDivElement>(null);
    const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);
    const loadMoreTriggerRef = useRef<HTMLDivElement>(null); 
    const { accessToken } = useAuth();

    // Calculate masonry layout based on screen size
    const calculateMasonryColumns = useCallback(() => {
        const width = window.innerWidth;
        let columnCount = 1;
        
        if (width >= 1024) {
            columnCount = 4;
        } else if (width >= 768) {
            columnCount = 3;
        } else if (width >= 640) {
            columnCount = 2;
        } else {
            columnCount = 2;
        }
        
        const columns: Array<Array<GalleryItem & { index: number }>> = Array.from(
            { length: columnCount }, 
            () => []
        );
        
        images.forEach((image, index) => {
            const columnIndex = index % columnCount;
            columns[columnIndex].push({ ...image, index });
        });
        
        return columns;
    }, [images]);

    // Handle responsive masonry
    useEffect(() => {
        const updateMasonry = () => {
            setMasonryColumns(calculateMasonryColumns());
        };
        
        updateMasonry();
        window.addEventListener('resize', updateMasonry);
        
        return () => window.removeEventListener('resize', updateMasonry);
    }, [calculateMasonryColumns]);

    // Infinite scroll implementation
    useEffect(() => {
        if (!enableInfiniteScroll || !isGalleryOpen || !hasMore || isLoadingMore || !getMorePhotos) {
            return;
        }

        const options = {
            root: null,
            rootMargin: '200px', // Trigger 200px before reaching the bottom
            threshold: 0.1
        };

        loadMoreObserverRef.current = new IntersectionObserver((entries) => {
            const target = entries[0];
            if (target.isIntersecting && hasMore && !isLoadingMore) {
                getMorePhotos();
            }
        }, options);

        if (loadMoreTriggerRef.current) {
            loadMoreObserverRef.current.observe(loadMoreTriggerRef.current);
        }

        return () => {
            if (loadMoreObserverRef.current) {
                loadMoreObserverRef.current.disconnect();
            }
        };
    }, [enableInfiniteScroll, isGalleryOpen, hasMore, isLoadingMore, getMorePhotos]);

    // Close gallery
    const closeGallery = useCallback(() => {
        setIsGalleryOpen(false);
        setSelectedIndex(null);
        setIsFullscreen(false);
    }, [setIsGalleryOpen]);

    // Open lightbox
    const openLightbox = useCallback((index: number) => {
        setSelectedIndex(index);
        setIsLoading(true);
    }, []);

    // Close lightbox
    const closeLightbox = useCallback(() => {
        setSelectedIndex(null);
        setIsFullscreen(false);
    }, []);

    // Navigation functions
    const goToPrevious = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        setSelectedIndex(prev => {
            if (prev === null) return null;
            return prev === 0 ? images.length - 1 : prev - 1;
        });
        setIsLoading(true);
    }, [images.length]);

    const goToNext = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        setSelectedIndex(prev => {
            if (prev === null) return null;
            return prev === images.length - 1 ? 0 : prev + 1;
        });
        setIsLoading(true);
    }, [images.length]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (selectedIndex !== null) {
                switch (e.key) {
                    case 'ArrowLeft':
                        goToPrevious();
                        break;
                    case 'ArrowRight':
                        goToNext();
                        break;
                    case 'Escape':
                        if (isFullscreen) {
                            setIsFullscreen(false);
                        } else {
                            closeLightbox();
                        }
                        break;
                    case 'f':
                    case 'F':
                        if (e.ctrlKey || e.metaKey) {
                            setIsFullscreen(!isFullscreen);
                        }
                        break;
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedIndex, goToPrevious, goToNext, closeLightbox, isFullscreen]);

    // Handle initial image index
    useEffect(() => {
        if (isGalleryOpen && initialImageIndex !== undefined) {
            setSelectedIndex(initialImageIndex);
        }
    }, [isGalleryOpen, initialImageIndex]);

    // Handle fullscreen
    useEffect(() => {
        if (isFullscreen) {
            lightboxRef.current?.requestFullscreen?.().catch(console.error);
        } else if (document.fullscreenElement) {
            document.exitFullscreen();
        }
    }, [isFullscreen]);

    // Download image
    const downloadImage = useCallback(async () => {
        if (selectedIndex === null) return;
        
        try {
            const image = images[selectedIndex];
            const response = await fetch(image.url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = image.label || `image-${selectedIndex + 1}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
        }
    }, [selectedIndex, images]);

    // Image loading handler
    const handleImageLoad = () => {
        setIsLoading(false);
    };

    // Handle swipe gestures for mobile
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            goToNext();
        } else if (isRightSwipe) {
            goToPrevious();
        }
        
        setTouchStart(null);
        setTouchEnd(null);
    };

    // Handle manual load more
    const handleLoadMore = async () => {
        if (getMorePhotos && !isLoadingMore && hasMore) {
            await getMorePhotos();
        }
    };

    if (!images || images.length === 0) {
        return null;
    }

    return (
        <>
            {/* Gallery Modal */}
            <AnimatePresence>
                {isGalleryOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-white z-50 overflow-y-auto"
                        style={{ overscrollBehavior: 'contain' }}
                    >
                        {/* Sticky Header */}
                        <div className="sticky top-0 bg-[#FFF8E7] backdrop-blur-sm border-b border-gray-200 z-10 shadow-sm">
                            <div className="max-w-7xl mx-auto px-1 sm:px-6 lg:px-8 py-1">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500">
                                            {images.length} {images.length === 1 ? 'photo' : 'photos'}
                                        </p>
                                    </div>
                                    <h4 className="text-center text-sm font-semibold text-gray-800">
                                        {name}
                                    </h4>
                                    <div className="flex items-center">
                                        {restaurantId && <button
                                            onClick={() => setIsUploadModalOpen(true)}
                                            className="hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-2"
                                            aria-label="Close gallery"
                                        >
                                            <ImagePlus className="w-4 h-4" />
                                        </button>}
                                        <button
                                            onClick={closeGallery}
                                            className="hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-2"
                                            aria-label="Close gallery"
                                        >
                                            <ChevronDown className="w-5 h-5" />
                                        </button>

                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Responsive Masonry Gallery */}
                        <div 
                            className="max-w-7xl mx-auto px-1 sm:px-2 lg:px-8 py-2"
                            ref={masonryContainerRef}
                        >
                            {/* Masonry Layout Container */}
                            <div className="flex gap-1 sm:gap-1 lg:gap-2">
                                {masonryColumns.map((column, columnIndex) => (
                                    <div 
                                        key={columnIndex} 
                                        className="flex-1 space-y-1 sm:space-y-1 lg:space-y-3"
                                    >
                                        {column.map(({ url, label, index }) => (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.03 }}
                                                className="relative cursor-pointer group"
                                                onClick={() => openLightbox(index)}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                <div className="relative overflow-hidden bg-gray-100 rounded-lg">
                                                    <img
                                                        src={url}
                                                        alt={label || `Gallery image ${index + 1}`}
                                                        className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-110"
                                                        loading="lazy"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                        <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-2 lg:p-3">
                                                            <span className="text-white font-medium text-xs sm:text-sm lg:text-base">
                                                                {label}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                ))}
                            </div>

                            {/* Loading indicator for infinite scroll */}
                            {enableInfiniteScroll && hasMore && (
                                <div 
                                    ref={loadMoreTriggerRef}
                                    className="flex justify-center items-center py-8"
                                >
                                    {isLoadingMore && (
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Loader2 className="w-6 h-6 animate-spin" />
                                            <span className="text-sm">Loading more photos...</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Manual Load More Button */}
                            {!enableInfiniteScroll && hasMore && (
                                <div className="flex justify-center py-8">
                                    <button
                                        onClick={handleLoadMore}
                                        disabled={isLoadingMore}
                                        className="px-6 py-3 bg-[#FF6B35] text-white rounded-lg font-medium hover:bg-[#FF5722] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                                    >
                                        {isLoadingMore ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                <span>Loading...</span>
                                            </>
                                        ) : (
                                            <span>Load More Photos</span>
                                        )}
                                    </button>
                                </div>
                            )}

                            {/* End of results message */}
                            {!hasMore && images.length > 0 && (
                                <div className="flex justify-center py-8">
                                    <p className="text-gray-500 text-sm">
                                        You've reached the end
                                    </p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Lightbox Viewer */}
            <AnimatePresence>
                {selectedIndex !== null && (
                    <motion.div
                        ref={lightboxRef}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={`fixed inset-0 z-[100] flex items-center justify-center ${
                            isFullscreen ? 'bg-black' : 'bg-black/95'
                        }`}
                        onClick={closeLightbox}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {/* Top Controls */}
                        <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 md:p-6 z-20">
                            <div className="text-white text-sm md:text-base font-medium bg-black/50 px-3 py-2 rounded backdrop-blur-sm">
                                {selectedIndex + 1} / {images.length}
                            </div>

                            <div className="flex items-center gap-2">
                                {restaurantId && <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsUploadModalOpen(true);
                                        closeLightbox();                                 
                                    }}
                                    className="p-2 text-white hover:bg-white/10 rounded transition-colors backdrop-blur-sm"
                                    aria-label="Upload image"
                                >
                                    <ImagePlus className="w-5 h-5 md:w-6 md:h-6" />
                                </button>}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        downloadImage();
                                    }}
                                    className="p-2 text-white hover:bg-white/10 rounded transition-colors backdrop-blur-sm"
                                    aria-label="Download image"
                                >
                                    <Download className="w-5 h-5 md:w-6 md:h-6" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsFullscreen(!isFullscreen);
                                    }}
                                    className="p-2 text-white hover:bg-white/10 rounded transition-colors backdrop-blur-sm"
                                    aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                                >
                                    <Maximize2 className="w-5 h-5 md:w-6 md:h-6" />
                                </button>
                                <button
                                    onClick={closeLightbox}
                                    className="p-2 text-white hover:bg-white/10 rounded transition-colors backdrop-blur-sm"
                                    aria-label="Close lightbox"
                                >
                                    <X className="w-5 h-5 md:w-6 md:h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Close button on top left (mobile) */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                closeLightbox();
                            }}
                            className="absolute top-4 left-4 p-2 text-white hover:bg-white/10 rounded transition-colors z-20 backdrop-blur-sm sm:hidden"
                            aria-label="Close lightbox"
                        >   <ChevronDown  className="w-5 h-5" />
                        </button>

                        {/* Navigation Buttons - Hidden on very small screens */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                goToPrevious();
                            }}
                            className="absolute left-2 md:left-6 p-2 md:p-3 text-white hover:bg-white/10 rounded transition-colors z-20 backdrop-blur-sm hidden sm:block"
                            aria-label="Previous image"
                        >
                            <ChevronLeft className="w-5 h-5 md:w-8 md:h-8" />
                        </button>

                        {/* Main Image Container */}
                        <motion.div
                            key={selectedIndex}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="relative max-w-7xl max-h-[85vh] mx-4 md:mx-8"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Loading State */}
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 rounded">
                                    <Loader2 className="w-8 h-8 md:w-12 md:h-12 text-white animate-spin" />
                                </div>
                            )}

                            {/* Image */}
                            <img
                                src={images[selectedIndex].url}
                                alt={images[selectedIndex].label || `Image ${selectedIndex + 1}`}
                                className={`max-w-full max-h-[85vh] w-auto h-auto object-contain rounded transition-opacity duration-300 ${
                                    isLoading ? 'opacity-0' : 'opacity-100'
                                }`}
                                onLoad={handleImageLoad}
                                loading="eager"
                            />

                            {/* Image Caption */}
                            {images[selectedIndex].label && (
                                <div className="text-center mt-4 md:mt-6">
                                    <p className="text-white text-base md:text-xl font-medium bg-black/50 px-4 py-2 rounded backdrop-blur-sm inline-block">
                                        {images[selectedIndex].label}
                                    </p>
                                </div>
                            )}
                        </motion.div>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                goToNext();
                            }}
                            className="absolute right-2 md:right-6 p-2 md:p-3 text-white hover:bg-white/10 rounded transition-colors z-20 backdrop-blur-sm hidden sm:block"
                            aria-label="Next image"
                        >
                            <ChevronRight className="w-5 h-5 md:w-8 md:h-8" />
                        </button>

                        {/* Bottom Navigation (Visible on mobile) */}
                        <div className="absolute bottom-4 md:bottom-8 left-0 right-0 flex justify-center gap-4 z-20 px-4 sm:hidden">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goToPrevious();
                                }}
                                className="px-4 py-2 text-white text-sm bg-black/50 rounded backdrop-blur-sm transition-colors hover:bg-black/70"
                            >
                                <MdArrowBackIosNew />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goToNext();
                                }}
                                className="px-4 py-2 text-white text-sm bg-black/50 rounded backdrop-blur-sm transition-colors hover:bg-black/70"
                            >
                                <MdArrowForwardIos />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

                {/* Upload Modal */}
                {isUploadModalOpen && (
                    <PhotoUpload 
                        isModalOpen={isUploadModalOpen}
                        setIsModalOpen={setIsUploadModalOpen}
                        restaurantId={restaurantId}
                        authToken={accessToken}
                        onUploadComplete={() => {
                            setIsUploadModalOpen(false);
                        }}
                    />
                )}
        </>
    );
};

export default OpenGalleryModal;